/**
 * Create Evaluation Page
 * Full-page responsive form for creating new evaluations with role-based workflows
 * Uses react-hook-form with Yup validation and Controller components
 */

import React, { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CheckCircle, Upload, FileText } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';


import {
  Button,
  Card,
  CardContent,
  CardHeader,
  PaginatedSelect,
  PaginatedSelectOption,
  VideoUpload,
} from '../../components/evaluations/createEvaluationComponents';
import { ROUTES , API_ENDPOINTS } from '../../constants';
import {
  CreateEvaluationFormData,
  UpdateEvaluationFormData,
  createEvaluationSchema,
  updateEvaluationSchema,
  defaultCreateEvaluationValues,
} from '../../schemas';
import { get } from '../../services/apiClient';
import {
  evaluationService,
  CreateEvaluationRequest,
  EvaluationUpdateRequest,
  EvaluationSummary,
  VideoUpload as VideoUploadType,
} from '../../services/evaluationService';
import { teachersService, Teacher } from '../../services/teachersService';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { cn } from '../../utils';

interface CreateEvaluationPageProps {
  className?: string;
}

type EvaluationFormValues =
  | CreateEvaluationFormData
  | UpdateEvaluationFormData;

interface EvaluationMetadata {
  subject?: string;
  grade_level?: string;
  lesson_title?: string;
  duration_minutes?: number;
  student_count?: number;
  special_considerations?: string;
  technology_used?: string | string[];
  assessment_methods?: string | string[];
}

interface ExistingEvaluation
  extends Omit<EvaluationSummary, 'metadata'> {
  evaluation_type?: 'formal' | 'informal' | 'summative';
  notes?: string | null;
  metadata?: EvaluationMetadata | null;
}

interface RubricTemplateOption {
  id: string;
  name?: string;
}

interface RubricTemplateResponse {
  data?: RubricTemplateOption[];
  pagination?: {
    total?: number;
    has_next?: boolean;
  };
}

const currentYear = new Date().getFullYear();
const academicYears = [
  `${currentYear - 5}-${currentYear - 4}`,
  `${currentYear - 4}-${currentYear - 3}`,
  `${currentYear - 3}-${currentYear - 2}`,
  `${currentYear - 2}-${currentYear - 1}`,
  `${currentYear}-${currentYear + 1}`,
  `${currentYear + 1}-${currentYear + 2}`,
  `${currentYear + 2}-${currentYear + 3}`,
  `${currentYear + 3}-${currentYear + 4}`,
  `${currentYear + 4}-${currentYear + 5}`,
];

// Steps configuration for the evaluation creation process
const getSteps = (
  isTeacher: boolean,
  isUpdate: boolean,
  currentStep: string
): Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'complete' | 'current' | 'upcoming';
}> => {
  if (isUpdate) {
    return [
      {
        id: 'form',
        title: 'Update Details',
        description: 'Edit evaluation information',
        icon: FileText,
        status: (currentStep === 'form'
          ? 'current'
          : currentStep === 'complete'
            ? 'complete'
            : 'upcoming') as 'complete' | 'current' | 'upcoming',
      },
      {
        id: 'complete',
        title: 'Complete',
        description: 'Evaluation updated successfully',
        icon: CheckCircle,
        status: (currentStep === 'complete' ? 'current' : 'upcoming') as
          'complete' | 'current' | 'upcoming',
      },
    ];
  }

  if (isTeacher) {
    return [
      {
        id: 'form',
        title: 'Create Evaluation',
        description: 'Fill in evaluation details',
        icon: FileText,
        status: (currentStep === 'form' ? 'current' : 'complete') as
          'complete' | 'current' | 'upcoming',
      },
      {
        id: 'video',
        title: 'Upload Video',
        description: 'Submit lesson recording',
        icon: Upload,
        status: (currentStep === 'video'
          ? 'current'
          : currentStep === 'complete'
            ? 'complete'
            : 'upcoming') as 'complete' | 'current' | 'upcoming',
      },
      {
        id: 'complete',
        title: 'Submit',
        description: 'Evaluation submitted for review',
        icon: CheckCircle,
        status: (currentStep === 'complete' ? 'current' : 'upcoming') as
          'complete' | 'current' | 'upcoming',
      },
    ];
  }

  // Admin flow
  return [
    {
      id: 'form',
      title: 'Create Evaluation',
      description: 'Set up evaluation details',
      icon: FileText,
      status: (currentStep === 'form' ? 'current' : 'complete') as
        'complete' | 'current' | 'upcoming',
    },
    {
      id: 'complete',
      title: 'Complete',
      description: 'Evaluation created successfully',
      icon: CheckCircle,
      status: (currentStep === 'complete' ? 'current' : 'upcoming') as
        'complete' | 'current' | 'upcoming',
    },
  ];
};

// Steps indicator component
interface StepsIndicatorProps {
  steps: Array<{
    id: string;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    status: 'complete' | 'current' | 'upcoming';
  }>;
  currentStep: string;
}

const StepsIndicator: React.FC<StepsIndicatorProps> = ({
  steps,
  currentStep,
}) => {
  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isComplete = index < currentStepIndex;
          const isCurrent = step.id === currentStep;
          const isUpcoming = index > currentStepIndex;

          return (
            <React.Fragment key={step.id}>
              {/* Step */}
              <div className="flex flex-col items-center space-y-1">
                {/* Step Icon */}
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-200',
                    isComplete && 'bg-green-500 border-green-500 text-white',
                    isCurrent && 'bg-blue-500 border-blue-500 text-white',
                    isUpcoming && 'bg-gray-100 border-gray-300 text-gray-400'
                  )}
                >
                  {isComplete ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>

                {/* Step Content */}
                <div className="text-center max-w-24">
                  <h3
                    className={cn(
                      'text-xs font-medium transition-colors duration-200',
                      isComplete && 'text-green-600',
                      isCurrent && 'text-blue-600',
                      isUpcoming && 'text-gray-500'
                    )}
                  >
                    {step.title}
                  </h3>
                  {/* <p
                    className={cn(
                      'text-xs mt-0.5 transition-colors duration-200 hidden sm:block',
                      isComplete && 'text-green-500',
                      isCurrent && 'text-blue-500',
                      isUpcoming && 'text-gray-400'
                    )}
                  >
                    {step.description}
                  </p> */}
                </div>
              </div>

              {/* Connector Line */}
              {/* {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2 transition-colors duration-200',
                    index < currentStepIndex && 'bg-green-500',
                    index >= currentStepIndex && 'bg-gray-300'
                  )}
                />
              )} */}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div>
        <p className="text-xs text-gray-500 mb-1.5 text-center">
          Step {currentStepIndex + 1} of {steps.length}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-1.5 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateEvaluationPage: React.FC<CreateEvaluationPageProps> = ({
  className,
}) => {
  const { user } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEvaluationId, setCreatedEvaluationId] = useState<string | null>(
    null
  );
  const [summaryEvaluation, setSummaryEvaluation] =
    useState<EvaluationSummary | null>(null);
  const [step, setStep] = useState<'details' | 'lesson' | 'video' | 'complete'>(
    'details'
  );
  const [existingEvaluation, setExistingEvaluation] =
    useState<ExistingEvaluation | null>(null);

  const isTeacher = user?.role === UserRole.TEACHER;
  const mode = searchParams.get('mode');
  const id = searchParams.get('id');
  const isUpdate = mode === 'edit' && !!id;

  // Load existing evaluation data when in update mode
  useEffect(() => {
    if (isUpdate && id) {
      const loadExistingEvaluation = async () => {
        try {
          setLoading(true);
          const evaluation = await evaluationService.getEvaluation(id);
          setExistingEvaluation(evaluation);
        } catch (error) {
          console.error('Error loading evaluation:', error);
          setError('Failed to load evaluation data');
        } finally {
          setLoading(false);
        }
      };
      loadExistingEvaluation();
    }
  }, [isUpdate, id]);

  // Function to load teachers for PaginatedSelect
  const loadTeachers = async (
    page: number,
    search?: string
  ): Promise<{
    data: PaginatedSelectOption[];
    hasMore: boolean;
    total?: number;
  }> => {
    try {
      const teachers = await teachersService.getTeachers({
        skip: (page - 1) * 10, // 10 items per page
        limit: 10,
        search: search || undefined,
      });

      const options: PaginatedSelectOption[] = teachers.map(
        (teacher: Teacher) => ({
          label: teacher.full_name || teacher.username,
          value: teacher.id.toString(),
        })
      );

      return {
        data: options,
        hasMore: teachers.length === 10, // If we got 10 items, there might be more
        total: teachers.length,
      };
    } catch (error) {
      console.error('Error loading teachers:', error);
      return {
        data: [],
        hasMore: false,
        total: 0,
      };
    }
  };

  // Function to load rubric templates for PaginatedSelect
  const loadTemplates = async (
    page: number,
    search?: string
  ): Promise<{
    data: PaginatedSelectOption[];
    hasMore: boolean;
    total?: number;
  }> => {
    try {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * 10).toString());
      params.append('limit', '10');
      if (search) params.append('search', search);

      const endpoint = `${API_ENDPOINTS.RUBRICS.TEMPLATES}?${params.toString()}`;
      const response = await get<RubricTemplateResponse>(endpoint);

      // Handle paginated response structure
      const list = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination;

      const options: PaginatedSelectOption[] = list.map(t => ({
        label: t.name || t.id,
        value: t.id,
      }));

      // Use pagination metadata if available
      const total = pagination?.total ?? list.length;
      const hasMore = pagination?.has_next ?? list.length === 10;

      return { data: options, hasMore, total };
    } catch (error) {
      console.error('Error loading templates:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  };

  // Initialize react-hook-form with Yup validation
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm<EvaluationFormValues>({
    resolver: yupResolver(
      isUpdate ? updateEvaluationSchema : createEvaluationSchema
    ) as NonNullable<
      Parameters<typeof useForm<EvaluationFormValues>>[0]
    >['resolver'],
    defaultValues: {
      ...defaultCreateEvaluationValues,
      teacher_id:
        user?.role === UserRole.TEACHER ? user.id?.toString() || '' : '',
      evaluation_period: academicYears[0] || '2024-2025',
    },
    shouldUnregister: false,
    mode: 'onChange', // Validate on change for better UX
  });

  // Update form values when existing evaluation data is loaded
  useEffect(() => {
    if (isUpdate && existingEvaluation) {
      const metadata = existingEvaluation.metadata ?? {};
      reset({
        teacher_id: existingEvaluation.teacher_id?.toString() || '',
        rubric_template_id:
          existingEvaluation.template_id ||
          existingEvaluation.rubric_template_id ||
          '',
        evaluation_period:
          existingEvaluation.evaluation_period || academicYears[0],
        evaluation_type: existingEvaluation.evaluation_type || 'formal',
        notes: existingEvaluation.notes || null,
        status: existingEvaluation.status || 'draft',
        // Lesson details from metadata
        subject: metadata.subject || '',
        grade_level: metadata.grade_level || '',
        lesson_title: metadata.lesson_title || '',
        duration_minutes: metadata.duration_minutes?.toString() || '45',
        student_count: metadata.student_count?.toString() || '24',
        special_considerations: metadata.special_considerations || '',
        technology_used: Array.isArray(metadata.technology_used)
          ? metadata.technology_used.join(', ')
          : metadata.technology_used || '',
        assessment_methods: Array.isArray(metadata.assessment_methods)
          ? metadata.assessment_methods.join(', ')
          : metadata.assessment_methods || '',
      });
    }
  }, [isUpdate, existingEvaluation, reset]);

  // Form submission handler
  const onSubmit = async (
    data: CreateEvaluationFormData | UpdateEvaluationFormData
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Prevent accidental submit from first step in any mode; advance to lesson step instead
      if (step !== 'lesson') {
        const valid = await trigger([
          'teacher_id',
          'rubric_template_id',
          'evaluation_period',
          'evaluation_type',
        ]);
        if (valid) {
          setStep('lesson');
        }
        setLoading(false);
        return;
      }

      if (isUpdate && id) {
        // Update existing evaluation
        const request: EvaluationUpdateRequest = {
          teacher_id: data.teacher_id ? Number(data.teacher_id) : undefined,
          rubric_template_id: data.rubric_template_id,
          evaluation_period: data.evaluation_period,
          evaluation_type: data.evaluation_type,
          evaluator_notes: data.notes || undefined,
          status: data.status || undefined,
        };

        const response = await evaluationService.updateEvaluation(id, request);
        setCreatedEvaluationId(response.id);
        setSummaryEvaluation(response);
        toast.success('Evaluation updated successfully');
        setStep('complete');
      } else {
        // Create new evaluation
        const formData = data as CreateEvaluationFormData;
        if (!formData.rubric_template_id || !formData.evaluation_period) {
          throw new Error('Rubric template and evaluation period are required');
        }

        const request: CreateEvaluationRequest = {
          teacher_id: Number(formData.teacher_id),
          rubric_template_id: formData.rubric_template_id,
          evaluation_period: formData.evaluation_period,
          status: formData.status || 'draft',
          metadata: {
            subject: formData.subject,
            grade_level: formData.grade_level,
            lesson_title: formData.lesson_title,
            observation_type: formData.evaluation_type,
            duration_minutes: parseInt(formData.duration_minutes) || 45,
            student_count: parseInt(formData.student_count) || 24,
            special_considerations:
              formData.special_considerations || undefined,
            technology_used: formData.technology_used
              ? formData.technology_used
                  .split(',')
                  .map((item: string) => item.trim())
              : [],
            assessment_methods: formData.assessment_methods
              ? formData.assessment_methods
                  .split(',')
                  .map((item: string) => item.trim())
              : [],
            notes: formData.notes || undefined,
            created_by: user?.id,
            created_at: new Date().toISOString(),
          },
        };

        const response = await evaluationService.createEvaluation(request);
        setCreatedEvaluationId(response.evaluation_id);
        try {
          const ev = await evaluationService.getEvaluation(
            response.evaluation_id
          );
          setSummaryEvaluation(ev);
        } catch {}
        toast.success('Evaluation created successfully');

        // For teachers, move to video upload step
        if (isTeacher) {
          setStep('video');
        } else {
          setStep('complete');
        }
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isUpdate ? 'update' : 'create'} evaluation`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUploadComplete = (videoUpload: VideoUploadType) => {
    setStep('complete');
    console.log(
      'Video uploaded for evaluation:',
      createdEvaluationId,
      videoUpload.id
    );
  };

  const handleSkipVideo = () => {
    setStep('complete');
  };

  const handleViewEvaluation = () => {
    if (createdEvaluationId) {
      navigate(ROUTES.EVALUATION_DETAIL.replace(':id', createdEvaluationId));
    }
  };

  const handleCreateAnother = () => {
    navigate(ROUTES.EVALUATIONS_CREATE);
    reset({
      ...defaultCreateEvaluationValues,
      teacher_id:
        user?.role === UserRole.TEACHER ? user.id?.toString() || '' : '',
      evaluation_period: academicYears[0] || '2024-2025',
      status: 'draft',
      subject: '',
      grade_level: '',
      lesson_title: '',
      duration_minutes: '',
      student_count: '',
      special_considerations: '',
      technology_used: '',
      assessment_methods: '',
    });
    setError(null);
    setCreatedEvaluationId(null);
    setStep('details');
  };

  // Get steps configuration
  const steps = getSteps(isTeacher, isUpdate, step);

  // Multi-step Form: Step 1 (details) and Step 2 (lesson)
  if (step === 'details' || step === 'lesson') {
    return (
      <div className={cn('space-y-6', className)}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {isUpdate
                ? 'Update Teacher Evaluation'
                : isTeacher
                  ? 'Submit New Evaluation'
                  : 'Create Teacher Evaluation'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isUpdate
                ? 'Edit the evaluation details below'
                : isTeacher
                  ? 'Create a new evaluation submission with video upload'
                  : 'Set up a new teacher evaluation with rubric and timeline'}
            </p>
          </div>
        </div>

        {/* Local two-step header for the form section */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setStep('details')}
                className={cn(
                  'text-sm font-medium px-3 py-1 rounded',
                  step === 'details'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                )}
              >
                1. Evaluation Details
              </button>
              <button
                type="button"
                onClick={() => setStep('lesson')}
                className={cn(
                  'text-sm font-medium px-3 py-1 rounded',
                  step === 'lesson'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                )}
              >
                2. Lesson Details
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Form container */}
        <div className="w-full">
          <Card className="shadow-lg">
            <CardHeader className="bg-gray-50 border-b border-gray-200 p-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {step === 'details' ? 'Evaluation Details' : 'Lesson Details'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {step === 'details'
                  ? 'Fill in the evaluation info, framework, and academic year'
                  : 'Provide lesson-specific details used for evaluation'}
              </p>
            </CardHeader>
            <CardContent className="py-2">
              <form
                onSubmit={e => {
                  // Disable native form submit entirely; we submit explicitly from the final button
                  e.preventDefault();
                }}
                className="space-y-6 sm:space-y-8"
              >
                {step === 'details' && (
                  <>
                    {/* Teacher Selection (Admin only) */}
                    {!isTeacher && (
                      <div className="space-y-2">
                        <Controller
                          name="teacher_id"
                          control={control}
                          render={({ field }) => (
                            <PaginatedSelect
                              label="Teacher"
                              placeholder="Search and select a teacher..."
                              value={field.value}
                              onChange={value => field.onChange(value)}
                              loadOptions={loadTeachers}
                              searchable={true}
                              searchPlaceholder="Search teachers by name or email..."
                              error={errors.teacher_id?.message}
                              helperText="Search and select the teacher to be evaluated"
                              required
                              inputSize="md"
                            />
                          )}
                        />
                      </div>
                    )}

                    {/* Rubric Template */}
                    <div className="space-y-2">
                      <Controller
                        name="rubric_template_id"
                        control={control}
                        render={({ field }) => (
                          <PaginatedSelect
                            label="Evaluation Framework"
                            placeholder="Search and select a template..."
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            loadOptions={loadTemplates}
                            searchable={true}
                            searchPlaceholder="Search templates by name..."
                            error={errors.rubric_template_id?.message}
                            helperText="Search and select the framework to use"
                            required
                            inputSize="md"
                          />
                        )}
                      />
                    </div>

                    {/* Evaluation Period */}
                    <div className="space-y-2">
                      <label
                        htmlFor="evaluation_period"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Academic Year <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="evaluation_period"
                        control={control}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="evaluation_period"
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.evaluation_period
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.evaluation_period}
                            aria-describedby={
                              errors.evaluation_period
                                ? 'evaluation_period-error'
                                : undefined
                            }
                          >
                            {academicYears.map(year => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      {errors.evaluation_period && (
                        <p
                          id="evaluation_period-error"
                          className="text-sm text-red-600"
                        >
                          {errors.evaluation_period.message}
                        </p>
                      )}
                    </div>

                    {/* Evaluation Type */}
                    <div className="space-y-2">
                      <label
                        htmlFor="evaluation_type"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Evaluation Type
                      </label>
                      <Controller
                        name="evaluation_type"
                        control={control}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="evaluation_type"
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.evaluation_type
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.evaluation_type}
                            aria-describedby={
                              errors.evaluation_type
                                ? 'evaluation_type-error'
                                : undefined
                            }
                          >
                            <option value="formal">Formal Observation</option>
                            <option value="informal">
                              Informal Observation
                            </option>
                            <option value="summative">
                              Summative Evaluation
                            </option>
                          </select>
                        )}
                      />
                      {errors.evaluation_type && (
                        <p
                          id="evaluation_type-error"
                          className="text-sm text-red-600"
                        >
                          {errors.evaluation_type.message}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {step === 'lesson' && (
                  <div className="pt-0">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Lesson Details
                    </h3>

                    {/* Subject */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="subject"
                            type="text"
                            placeholder="e.g., Science, Math, English"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.subject
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.subject}
                          />
                        )}
                      />
                      {errors.subject && (
                        <p className="text-sm text-red-600">
                          {errors.subject.message}
                        </p>
                      )}
                    </div>

                    {/* Grade Level */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="grade_level"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Grade Level <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="grade_level"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="grade_level"
                            type="text"
                            placeholder="e.g., 5th Grade, 9th Grade"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.grade_level
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.grade_level}
                          />
                        )}
                      />
                      {errors.grade_level && (
                        <p className="text-sm text-red-600">
                          {errors.grade_level.message}
                        </p>
                      )}
                    </div>

                    {/* Lesson Title */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="lesson_title"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Lesson Title <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="lesson_title"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="lesson_title"
                            type="text"
                            placeholder="e.g., Ecosystems and Food Chains"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.lesson_title
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.lesson_title}
                          />
                        )}
                      />
                      {errors.lesson_title && (
                        <p className="text-sm text-red-600">
                          {errors.lesson_title.message}
                        </p>
                      )}
                    </div>

                    {/* Duration */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="duration_minutes"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Duration (minutes){' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="duration_minutes"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="duration_minutes"
                            type="number"
                            min="1"
                            max="180"
                            placeholder="45"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.duration_minutes
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.duration_minutes}
                          />
                        )}
                      />
                      {errors.duration_minutes && (
                        <p className="text-sm text-red-600">
                          {errors.duration_minutes.message}
                        </p>
                      )}
                    </div>

                    {/* Student Count */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="student_count"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Student Count <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="student_count"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="student_count"
                            type="number"
                            min="1"
                            max="50"
                            placeholder="24"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.student_count
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.student_count}
                          />
                        )}
                      />
                      {errors.student_count && (
                        <p className="text-sm text-red-600">
                          {errors.student_count.message}
                        </p>
                      )}
                    </div>

                    {/* Special Considerations */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="special_considerations"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Special Considerations
                      </label>
                      <Controller
                        name="special_considerations"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            id="special_considerations"
                            placeholder="e.g., ELL students present, IEP accommodations"
                            rows={2}
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical',
                              errors.special_considerations
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.special_considerations}
                          />
                        )}
                      />
                      {errors.special_considerations && (
                        <p className="text-sm text-red-600">
                          {errors.special_considerations.message}
                        </p>
                      )}
                    </div>

                    {/* Technology Used */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="technology_used"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Technology Used
                      </label>
                      <Controller
                        name="technology_used"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="technology_used"
                            type="text"
                            placeholder="e.g., Smart Board, Digital Microscope (comma-separated)"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.technology_used
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.technology_used}
                          />
                        )}
                      />
                      {errors.technology_used && (
                        <p className="text-sm text-red-600">
                          {errors.technology_used.message}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Separate multiple items with commas
                      </p>
                    </div>

                    {/* Assessment Methods */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="assessment_methods"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Assessment Methods
                      </label>
                      <Controller
                        name="assessment_methods"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            id="assessment_methods"
                            type="text"
                            placeholder="e.g., observation, student_work_samples (comma-separated)"
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.assessment_methods
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.assessment_methods}
                          />
                        )}
                      />
                      {errors.assessment_methods && (
                        <p className="text-sm text-red-600">
                          {errors.assessment_methods.message}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Separate multiple methods with commas
                      </p>
                    </div>

                    {/* Status */}
                    <div className="space-y-2 mb-4">
                      <label
                        htmlFor="status"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Status
                      </label>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <select
                            {...field}
                            id="status"
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                              errors.status
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.status}
                            aria-describedby={
                              errors.status ? 'status-error' : undefined
                            }
                          >
                            <option value="draft">Draft</option>
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="scored">Scored</option>
                            <option value="completed">Completed</option>
                            <option value="approved">Approved</option>
                            <option value="archived">Archived</option>
                          </select>
                        )}
                      />
                      {errors.status && (
                        <p id="status-error" className="text-sm text-red-600">
                          {errors.status.message}
                        </p>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <label
                        htmlFor="notes"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Additional Notes
                      </label>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            id="notes"
                            placeholder="e.g., Focus on student engagement and questioning techniques"
                            rows={3}
                            value={field.value || ''}
                            className={cn(
                              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical',
                              errors.notes
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            )}
                            aria-invalid={!!errors.notes}
                            aria-describedby={
                              errors.notes ? 'notes-error' : 'notes-help'
                            }
                          />
                        )}
                      />
                      {errors.notes && (
                        <p id="notes-error" className="text-sm text-red-600">
                          {errors.notes.message}
                        </p>
                      )}
                      <p id="notes-help" className="text-sm text-gray-600">
                        Optional: Add context or special instructions
                      </p>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="text-red-500 mr-3">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {/* Step Controls */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 pt-6 border-t border-gray-200">
                  <div>
                    {step === 'lesson' && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setStep('details')}
                        disabled={isSubmitting || loading}
                        className="w-full sm:w-auto"
                      >
                        Back
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => window.history.back()}
                      disabled={isSubmitting || loading}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    {step === 'details' ? (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={async () => {
                          const valid = await trigger([
                            'teacher_id',
                            'rubric_template_id',
                            'evaluation_period',
                            'evaluation_type',
                          ]);
                          if (valid) setStep('lesson');
                        }}
                        disabled={isSubmitting || loading}
                        className="w-full sm:w-auto"
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={isSubmitting || loading}
                        loading={isSubmitting || loading}
                        className="w-full sm:w-auto"
                        onClick={() => handleSubmit(onSubmit)()}
                      >
                        {loading
                          ? isUpdate
                            ? 'Updating...'
                            : 'Creating...'
                          : isUpdate
                            ? 'Update Evaluation'
                            : 'Create Evaluation'}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 2: Video Upload (Teachers only)
  if (step === 'video') {
    return (
      <div className={cn('space-y-6', className)}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Evaluation {isUpdate ? 'Updated' : 'Created'} Successfully!
              </h1>
            </div>
            <p className="text-gray-600 mt-1">
              {isUpdate
                ? 'Your evaluation has been updated successfully.'
                : 'Now upload your lesson video to complete the submission.'}
            </p>
          </div>
        </div>

        {/* Steps Indicator */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <StepsIndicator steps={steps} currentStep="video" />
          </CardContent>
        </Card>

        {/* Video upload container */}
        <div className="w-full">
          {/* Video Upload */}
          <VideoUpload
            teacherId={watch('teacher_id') || ''}
            evaluationPurpose={`Evaluation ${createdEvaluationId}`}
            onUploadComplete={handleVideoUploadComplete}
            onCancel={() => setError('Upload cancelled')}
          />

          {/* Skip Option */}
          <div className="text-center mt-6">
            <Button variant="secondary" onClick={handleSkipVideo}>
              Skip Video Upload for Now
            </Button>
            <p className="text-sm text-gray-600 mt-2">
              You can upload your video later from the evaluation details page.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <Card className="mt-6">
              <CardContent>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-red-500 mr-3">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Step 3: Complete
  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {isTeacher ? 'Evaluation Submitted!' : 'Evaluation Created!'}
            </h1>
          </div>
          <p className="text-gray-600 mt-1">
            {isTeacher
              ? 'Your evaluation has been submitted and will be reviewed by your administrator.'
              : 'The teacher evaluation has been created and is ready for video submission.'}
          </p>
        </div>
      </div>

      {/* Steps Indicator */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <StepsIndicator steps={steps} currentStep="complete" />
        </CardContent>
      </Card>

      {/* Content container */}
      <div className="w-full">
        {/* Summary Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50 border-b border-gray-200 p-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Evaluation Summary
            </h2>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Teacher:</span>
                  <span className="font-medium text-gray-900">
                    {summaryEvaluation?.teacher?.full_name ||
                      (isTeacher ? user?.full_name || user?.username : '')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Framework:</span>
                  <span className="font-medium text-gray-900">
                    {summaryEvaluation?.template?.name || ''}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Academic Year:</span>
                  <span className="font-medium text-gray-900">
                    {watch('evaluation_period')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium text-gray-900 capitalize">
                    {watch('evaluation_type')}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
          <Button
            variant="primary"
            onClick={handleViewEvaluation}
            className="w-full sm:w-auto"
          >
            View Evaluation
          </Button>
          <Button
            variant="secondary"
            onClick={handleCreateAnother}
            className="w-full sm:w-auto"
          >
            Create Another
          </Button>
        </div>
      </div>
    </div>
  );
};
